import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Hourglass, Lock, LockOpen } from 'lucide-react';
import { Field, formatDateTime } from '@/components/live';
import { ApprovalPinField, PIN_RESULTS, isPinResult } from '@/components/ApprovalPin';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

interface ApprovalState {
  locked: boolean;
  locked_at: string | null;
  locked_by_name: string | null;
  locked_reason: string | null;
  can_lock: boolean;
  can_unlock: boolean;
  request_id: string | null;
  request_status: string | null;
  submitted_at: string | null;
  submitted_by_name: string | null;
  is_admin: boolean;
}

/**
 * Where a record stands in the change approval (migration 0065): waiting on the next level up,
 * approved and locked, or open. An admin can lock it; a role above the locker (or an admin) can
 * unlock it, with a reason. Both take the approval PIN.
 */
export function RecordLockBar({ entityTable, entityId, invalidate = [] }: {
  entityTable: string; entityId: string; invalidate?: string[];
}) {
  const queryClient = useQueryClient();
  const [acting, setActing] = useState<'lock' | 'unlock' | null>(null);
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const state = useQuery({
    queryKey: ['record-approval-state', entityTable, entityId],
    queryFn: () => callRpc<ApprovalState | null>('fn_record_approval_state', { p_entity_table: entityTable, p_entity_id: entityId }),
    enabled: isSupabaseConfigured,
  });
  const s = state.data;
  if (!s) return null;

  const pending = !s.locked && s.request_status && ['submitted', 'under_review', 'returned'].includes(s.request_status);

  async function act() {
    if (acting === 'unlock' && !reason.trim()) { setError('Say why it is being unlocked.'); return; }
    if (!pin) { setError('Enter your approval PIN.'); return; }
    setError(undefined);
    setBusy(true);
    try {
      const outcome = acting === 'lock'
        ? await callRpc<string>('fn_lock_record', { p_entity_table: entityTable, p_entity_id: entityId, p_pin: pin, p_reason: reason.trim() || null })
        : await callRpc<string>('fn_unlock_record', { p_entity_table: entityTable, p_entity_id: entityId, p_reason: reason.trim(), p_pin: pin });
      if (isPinResult(outcome)) { setError(PIN_RESULTS[outcome]); setPin(''); return; }
      toast.success(outcome === 'locked' ? 'Locked.' : outcome === 'unlocked' ? 'Unlocked. The next change goes for approval again.' : outcome);
      setActing(null); setReason(''); setPin('');
      await queryClient.invalidateQueries({ queryKey: ['record-approval-state', entityTable, entityId] });
      await queryClient.invalidateQueries({ queryKey: ['approval_inbox'] });
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: [key] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be done.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`record-lock-bar ${s.locked ? 'locked' : pending ? 'pending' : 'open'}`}>
      <div className="record-lock-line">
        {s.locked ? (
          <span><Lock size={15} /> <strong>Approved and locked</strong>
            {s.locked_by_name ? ` by ${s.locked_by_name}` : ''}{s.locked_at ? `, ${formatDateTime(s.locked_at)}` : ''}.
            {' '}Not editable until a higher role unlocks it.</span>
        ) : pending ? (
          <span><Hourglass size={15} /> <strong>{s.request_status === 'returned' ? 'Returned for changes' : 'Awaiting approval'}</strong>
            {' '}by the next level up{s.submitted_by_name ? ` - changed by ${s.submitted_by_name}` : ''}
            {s.submitted_at ? `, ${formatDateTime(s.submitted_at)}` : ''}.</span>
        ) : (
          <span><LockOpen size={15} /> {s.is_admin ? 'Open. As an admin your changes need no approval - lock it when it is final.' : 'Open. Your changes go to the next level up for approval.'}</span>
        )}
        {!acting && s.can_lock && <button onClick={() => setActing('lock')} type="button"><Lock size={14} /> Lock</button>}
        {!acting && s.can_unlock && <button onClick={() => setActing('unlock')} type="button"><LockOpen size={14} /> Unlock</button>}
      </div>
      {acting && (
        <div className="inline-form">
          <Field label={acting === 'unlock' ? 'Why unlock it?' : 'Note (optional)'} required={acting === 'unlock'}>
            <input onChange={(event) => setReason(event.target.value)} value={reason} />
          </Field>
          <ApprovalPinField onChange={setPin} value={pin} />
          {error && <p className="login-error" role="alert">{error}</p>}
          <div className="inline-form-actions">
            <button className="cancel" onClick={() => { setActing(null); setError(undefined); }} type="button">Cancel</button>
            <button className="primary" disabled={busy} onClick={() => void act()} type="button">
              {busy ? 'Working...' : acting === 'lock' ? 'Lock record' : 'Unlock record'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
